export const generateBinaryQuestionPrompt = (eventDetails, stats, posts = []) => {
  console.log('⚙️ generateBinaryQuestionPrompt called with:', {
    hasEventDetails: !!eventDetails,
    eventDetailsKeys: eventDetails ? Object.keys(eventDetails) : [],
    hasStats: !!stats,
    statsKeys: stats ? Object.keys(stats) : [],
    hasPosts: !!posts,
    postsCount: Array.isArray(posts) ? posts.length : 0,
    timestamp: new Date().toISOString()
  });

  if (!eventDetails) {
    console.error('❌ Missing eventDetails in generateBinaryQuestionPrompt');
    return null;
  }

  if (!stats) {
    console.warn('⚠️ Missing stats in generateBinaryQuestionPrompt, continuing with limited data');
  }

  try {
    // Format event details and stats for the prompt
    const eventName = eventDetails?.name || 'N/A';
    const questionType = eventDetails?.question_type || 'binary';
    const description = eventDetails?.description || 'N/A';
    
    // Access metrics data from the correct path
    const metricsData = stats?.metrics || {};
    const summaryData = stats?.summary || {};
    
    console.log('📊 Binary prompt - accessing metrics:', { 
      hasMetrics: !!metricsData, 
      metricsDataKeys: Object.keys(metricsData),
      hasSummary: !!summaryData,
      summaryDataKeys: Object.keys(summaryData),
      timestamp: new Date().toISOString() 
    });
    
    // Extract statistics using multiple fallback paths
    const totalResponses = metricsData.contributed || metricsData.choices || metricsData.ideas || 0;
    
    console.log('🔎 Binary question - using direct stats from Results tab:', {
      responses: totalResponses,
      timestamp: new Date().toISOString()
    });
    
    // Apply same approach for total users
    let totalUsers = metricsData.total_user || metricsData.viewed_user || metricsData.joined || 0;
    let participationRate = totalUsers > 0 ? Math.round((totalResponses / totalUsers) * 100) : 0;
    
    // Try to extract binary responses from all possible locations with exhaustive fallbacks
    console.log('📊 Binary question - examining summary and metrics for yes/no data:', { 
      summaryData, 
      metricsData,
      originalStats: stats,
      timestamp: new Date().toISOString() 
    });
    
    // Examine summary.ideas for binary choices
    let yesCount = 0;
    let noCount = 0;
    
    // Check if summary.ideas exists
    if (summaryData.ideas && Array.isArray(summaryData.ideas)) {
      const yesIdea = summaryData.ideas.find(i => i.title && i.title.toLowerCase() === 'yes');
      const noIdea = summaryData.ideas.find(i => i.title && i.title.toLowerCase() === 'no');
      
      if (yesIdea) {
        yesCount = yesIdea.choice_count || 0;
      }
      
      if (noIdea) {
        noCount = noIdea.choice_count || 0;
      }
      
      console.log('📊 Binary question - found in summary.ideas:', { yesCount, noCount });
    }
    
    // If still zero, check choices field which often contains choice counts
    if (yesCount === 0 && noCount === 0 && summaryData.choices) {
      const choices = summaryData.choices;
      Object.keys(choices || {}).forEach(key => {
        if (key.toLowerCase() === 'yes' || key.toLowerCase() === 'true') {
          yesCount = choices[key];
        } else if (key.toLowerCase() === 'no' || key.toLowerCase() === 'false') {
          noCount = choices[key];
        }
      });
      
      console.log('📊 Binary question - found in summary.choices:', { yesCount, noCount });
    }
    
    // Final fallbacks
    if (yesCount === 0) {
      yesCount = summaryData.yes_count || metricsData.yes_count || summaryData.yes || metricsData.yes || 0;
    }
    
    if (noCount === 0) {
      noCount = summaryData.no_count || metricsData.no_count || summaryData.no || metricsData.no || 0;
    }
    
    // If we have a total response count but no yes/no counts, estimate them
    if (yesCount === 0 && noCount === 0 && totalResponses > 0) {
      // Since we know there are responses but can't find yes/no counts,
      // let's use contributed count as a fallback and split 50/50
      yesCount = Math.round(totalResponses / 2);
      noCount = totalResponses - yesCount;
      console.log('📊 Binary question - estimating from total responses:', { totalResponses, yesCount, noCount });
    }
    
    // Cross-verify totalResponses with sum of yes/no counts
    if (totalResponses === 0 && (yesCount > 0 || noCount > 0)) {
      totalResponses = yesCount + noCount;
      console.log('📊 Binary question - corrected totalResponses:', { totalResponses, yesCount, noCount });
    }
    
    // Calculate percentages from raw counts
    let yesPercentage = totalResponses > 0 ? Math.round((yesCount / totalResponses) * 100) : 0;
    let noPercentage = totalResponses > 0 ? Math.round((noCount / totalResponses) * 100) : 0;
    
    // Ensure percentages add up to 100%
    if (yesPercentage + noPercentage !== 100 && totalResponses > 0) {
      // Adjust the smaller percentage to ensure they sum to 100%
      if (yesPercentage > noPercentage) {
        noPercentage = 100 - yesPercentage;
      } else {
        yesPercentage = 100 - noPercentage;
      }
    }
    
    console.log('🔢 Binary question - using direct stats:', {
      yes_count: yesCount,
      no_count: noCount,
      yes_percentage: yesPercentage,
      no_percentage: noPercentage,
      timestamp: new Date().toISOString()
    });
    
    // Extract binary-specific data (Yes/No distribution)
    // Get data about comments and engagement directly
    const comments = stats?.comments || 0;
    const likes = stats?.comment_likes || 0;
    const dislikes = stats?.comment_dislikes || 0;
    
    // Use engagement_rate directly from stats or calculate it simply
    let engagementRate = stats?.engagement_rate || 0;
    if (typeof engagementRate === 'number' && engagementRate <= 1) {
      engagementRate = Math.round(engagementRate * 100); // Convert from decimal to percentage
    }
    
    // Use most_engaged_post directly if available
    const rawMostEngagedPost = stats?.most_engaged_post;
    const engagementInfo = rawMostEngagedPost ? `The most engaged post received ${rawMostEngagedPost.engagements || 0} engagements.` : '';
    
    console.log('👥 Binary question - direct engagement metrics:', {
      comments,
      likes,
      dislikes,
      engagement_rate: engagementRate,
      timestamp: new Date().toISOString()
    });
    
    // Extract user segment data directly
    const userSegments = {
      basicUsers: stats?.basic_user || 0,
      heroUsers: stats?.hero_user || 0,
      earlyAccessUsers: stats?.early_access_user || 0,
      observerUsers: stats?.observer_user || 0
    };
    
    // Extract platform distribution directly
    const platformDistribution = {
      desktop: stats?.desktop_user || 0,
      ios: stats?.ios_user || 0,
      android: stats?.android_user || 0,
      mobileWeb: stats?.mobile_web_user || 0
    };
    
    // Calculate the decisiveness ratio (how clear-cut the decision is)
    const decisivenessRatio = Math.abs(yesPercentage - noPercentage);
    const decisivenessLevel = decisivenessRatio > 50 ? 'Very Decisive' : 
                            decisivenessRatio > 30 ? 'Decisive' : 
                            decisivenessRatio > 15 ? 'Somewhat Decisive' : 'Closely Divided';
    
    // Format user comments to provide context for why users chose Yes/No
    let formattedComments = '';
    if (Array.isArray(posts) && posts.length > 0) {
      // Sort posts by engagement (likes + comments)
      const sortedPosts = [...posts].sort((a, b) => {
        const aEngagement = (a?.like_count || 0) + (a?.child_count || 0);
        const bEngagement = (b?.like_count || 0) + (b?.child_count || 0);
        return bEngagement - aEngagement; // Descending order
      });
      
      formattedComments = sortedPosts.map(post => {
        const content = post?.content || '';
        const likeCount = post?.like_count || 0;
        const childCount = post?.child_count || 0;
        
        // Format replies if they exist
        const replies = Array.isArray(post?.children) 
          ? post.children.map(child => {
              return `  - Reply: "${child?.content || ''}"'`;
            }).join('\n')
          : '';
        
        return `"${content}"${replies ? '\n' + replies : ''}`;
      }).join('\n\n');
    } else {
      formattedComments = 'No comments available';
    }

    // Format the data section - simplified to improve LLM results but including user comments
    const formattedData = `
Event Details:
- Name: ${eventName}
- Question Type: ${questionType}
- Description: ${description}

Response Distribution:
    Yes Responses: ${yesCount} (${yesPercentage}%)
    No Responses: ${noCount} (${noPercentage}%)

User Comments:
${formattedComments}
`;
    
    // Create binary question prompt template
    const promptTemplate = `You are an expert data analyst analyzing a Binary Choice survey. This type of survey presents respondents with exactly two options (typically Yes/No or Agree/Disagree). Analyze the following data and provide a structured report in markdown format:

## Event Analysis Data
${formattedData}

## Analysis Instructions
1. Focus on WHY respondents chose their answers, not just the distribution (which is shown in the UI)
2. Identify patterns in how different user segments responded
3. Analyze the relationship between response choice and engagement level
4. Evaluate the decisiveness of the result and what it indicates about consensus or division
5. Consider how the engagement metrics provide context for the strength of opinions
6. Look for any evidence of opinion leaders or influential responses that may have swayed others
7. Analyze what the binary choice reveals about underlying values, priorities, or concerns

## Report Format
Please format your response as follows:

# Executive Summary
[1-2 paragraph summary focusing on the key narrative emerging from responses]

# Analysis
- [Bullet points with insights about response patterns and their significance]
- [Include analysis of both explicit statements and implicit meanings]
- [Note any emotional undertones or sentiment patterns]
- [Highlight any subtle differences or variations in how respondents expressed themselves]

# Key Themes
- [List 3-5 major themes with examples from the responses]
- [Focus on underlying concerns, values, and priorities]
- [Include both positive and constructive feedback themes]
- [If engagement is present and nonzero, highlight which themes generated the most engagement]

# Recommendations
- [Provide 3-5 actionable next steps based on the feedback]
- [Include specific suggestions for addressing concerns]
- [Recommend strategies for leveraging positive feedback]
- [Suggest follow-up questions to explore identified themes further]

# Limitations
- [List any limitations in the data that affect the depth or breadth of analysis]
- [Suggest what additional data would be helpful]
- [Note any potential biases or gaps in the responses]

# Survey Design Feedback
- [Provide suggestions for improving the survey question or format]
- [Suggest how to generate richer, more actionable, or more diverse responses]

Format your response with these exact section headings using single # for each section. Ensure each section has substantive content.`;

console.log('✅ Binary prompt generated:', {
  promptLength: promptTemplate.length,
  timestamp: new Date().toISOString()
});

return promptTemplate;
} catch (error) {
  console.error('❌ Error in generateBinaryQuestionPrompt:', {
    error: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString()
  });

  // Create a simple fallback prompt
  let fallbackYesCount = stats?.yes || 0;
  let fallbackNoCount = stats?.no || 0;
  
  const totalFallbackResponses = fallbackYesCount + fallbackNoCount;
  const yesFallbackPercentage = totalFallbackResponses > 0 ? Math.round((fallbackYesCount / totalFallbackResponses) * 100) : 0;
  const noFallbackPercentage = totalFallbackResponses > 0 ? Math.round((fallbackNoCount / totalFallbackResponses) * 100) : 0;
  
  let dataSection = '';
  
  if (totalFallbackResponses > 0) {
    dataSection = `
Data Summary:
- Total Responses: ${totalFallbackResponses}
- Yes Responses: ${fallbackYesCount} (${yesFallbackPercentage}%)
- No Responses: ${fallbackNoCount} (${noFallbackPercentage}%)`;
    }
    
    const fallbackPrompt = `You are an expert data analyst analyzing a Binary (Yes/No) survey question.${dataSection} Please provide a general analysis of these results and best practices for interpreting such data.`;
    
    console.log('⚠️ Using fallback prompt due to error:', {
      fallbackPromptLength: fallbackPrompt.length,
      hasYesNoData: !!dataSection,
      timestamp: new Date().toISOString()
    });
    
    return fallbackPrompt;
  }
};